import { notEmpty, typeUtils } from '@festina-life/core'
import { useMemo } from 'react'

import type {
  RankedItem,
  SearchableItemCategory,
} from '../../components/search/commandPalette/types'
import { useHook } from '../../lenteHooks'
import { useIsAllowedToViewEntity } from '../annotation/useIsAllowedToViewEntity'
import { useCheckEntityRouting } from './useCheckEntityRouting'

type Props = {
  searchTerm: string
}

type SearchResults = RankedItem[]

/**
 * Searches for the given term everywhere in the application.
 */
export const useSearch = ({ searchTerm }: Props): SearchResults => {
  const searchCategories = useHook('routing.search.useSearchCategories')()
  const tenantCategories = useHook('routing.search.useTenantPagesCategories')()

  const tenantItems = useHook('routing.search.useTenantSearchableItems')()

  const personItems = useHook('routing.search.usePersonSearchableItems')()
  const companyItems = useHook('routing.search.useCompanySearchableItems')()
  const actionItems = useHook('routing.search.useActionSearchableItems')()
  const recentItems = useHook('routing.search.useRecentSearchableItems')()

  const { entity } = useCheckEntityRouting()
  const isUserAllowedToDoFetch = useIsAllowedToViewEntity(
    entity
      ? {
          businessType: entity,
        }
      : { enabled: false },
  )

  // All items, with only the categories that exist in the current layer.
  const allItems = useMemo(
    () =>
      [
        // Done only here for now, the others should also be refactored to objects, for easy re-use and re-ordering.
        ...Object.values(tenantItems ?? {}).filter(notEmpty),
        ...Object.values(tenantCategories ?? {}).filter(notEmpty),
        ...Object.values(
          isUserAllowedToDoFetch && personItems ? personItems : {},
        ).filter(notEmpty),
        ...Object.values(
          isUserAllowedToDoFetch && companyItems ? companyItems : {},
        ).filter(notEmpty),
        ...actionItems,
        ...recentItems,
      ].filter(
        (item): item is SearchableItemCategory[number] =>
          !!item.categories.length &&
          (!typeUtils.hasField(item, 'hasRequiredComponents') ||
            item.hasRequiredComponents),
      ),
    [
      isUserAllowedToDoFetch,
      actionItems,
      personItems,
      companyItems,
      recentItems,
      tenantCategories,
      tenantItems,
    ],
  )

  const lowercasedCategoryLabels = useMemo(
    () =>
      Object.entries(searchCategories)
        .map(([, categoryRecord]) =>
          Object.entries(categoryRecord).map(([category, renderedCategory]) => [
            category,
            renderedCategory.label.toLocaleLowerCase(),
          ]),
        )
        .flat(),
    [searchCategories],
  )

  return useMemo(() => {
    const searchTerms = searchTerm.trim().toLocaleLowerCase().split(/\s+/)

    return allItems.flatMap((item) => {
      const ranked = { ...item, rank: 0 }

      const title = item.title.toLocaleLowerCase()
      if (searchTerms.every((term) => title.includes(term))) {
        ranked.rank += 4 // Ensure title alone ranks higher than other matches combined.
      }

      if (
        item.keywords?.some((keyword) =>
          searchTerms.every((term) => keyword.includes(term)),
        )
      ) {
        ranked.rank += 2
      }

      if (item.categories.length) {
        const matchingCategories = item.categories.filter((category) =>
          searchTerms.every((term) => {
            const item = lowercasedCategoryLabels.find(
              (x) => x[0] === category,
            )?.[1]

            return item ? item.includes(term) : false
          }),
        )
        if (typeUtils.isNonEmptyArray(matchingCategories)) {
          // Keep only matching categories, otherwise search results would
          // show unrelated categories with zero matches in any of its items.
          ranked.categories = matchingCategories
          ranked.rank += 1
        }
      }

      return ranked.rank > 0 ? [ranked] : []
    })
  }, [allItems, lowercasedCategoryLabels, searchTerm])
}
